import { grownExpenses } from './expenseGrowth';
import { calculateNOI, projectNoiYear, type NoiProjectionInputs, type NoiYear } from './financialCalculations';
import type { DealAnalysis } from '../types';
/**
 * WHICH NOI EACH METRIC READS.
 *
 * ⭐⭐ THE DEFECT THESE EXIST FOR. Once a contractual rent schedule is applied, the proforma runs
 * on it - but the scorecard's DSCR and cash-on-cash kept reading the IN-PLACE NOI, and the exit
 * kept growing the last year by a GENERIC growth rate instead of using the lease. So one screen
 * carried up to three different ideas of the same deal's NOI.
 *
 * Measured on STIIIZY El Centro against Joseph's delivered analysis:
 *   DSCR    app 3.52x (386,355 / 109,650)   delivered 3.62x (396,980 / 109,650)
 *   exit    app grew the last year by 3%    delivered used the last hold year, $442,483
 *
 * ⛔ ONE PLACE, so the answer cannot differ between the scorecard, the projections, the PDF and
 * the scenario engine. That divergence is the defect class this build produces most often.
 */

/** A schedule as `noi_overrides` stores it: one entry per hold year, null where unknown. */
export type NoiScheduleLike = Array<number | null | undefined> | null | undefined;

const usable = (v: unknown): v is number => typeof v === 'number' && isFinite(v) && v > 0;

/**
 * ⭐⭐ THE IN-PLACE NOI - the RATE the property earns today, the going-in cap's basis and the debt
 * yield's. ⛔ Struck AFTER the carry is read: on a non-income asset the modelled expenses are a
 * CARRY, not operating expenses, so what it earns today is its EGI. The screen struck this as
 * `egi - totalExpenses` seven lines above the flag, and printed cap -1.75% and DSCR -0.29x on the
 * untouched Victorville deal beside a proforma reading NOI $0 with the carry on (JJ2).
 * ⭐ An anchor is a RATE, never a year of collections - a lease-up does not touch this.
 */
export const inPlaceNoi = (input: { egi: number; totalExpenses: number; expensesAreCarry: boolean }): number =>
  input.expensesAreCarry ? input.egi : calculateNOI(input.egi, input.totalExpenses);

/**
 * ⭐⭐⭐ THE NOI THAT YEAR-1 METRICS MUST READ - DSCR, year-1 cash flow, the Year 1 NOI card.
 *
 * It IS the projection's year 1, through the same function `generateProjections` runs, so the
 * strip and the proforma table cannot say two things about the same year. That means it honours
 * the applied schedule (STIIIZY: 396,980 and DSCR 3.62x, his), the LEASE-UP (Victorville: year 1
 * NOI $0 and DSCR 0.00x - his scorecard, where the app struck 1.40x on the stabilised figure) and
 * the CARRY (NOI $0, the carry reported beside it so cash flow can deduct it).
 *
 * ⚠️ Applying a schedule is opt-in, and with no schedule, no lease-up and no carry this is exactly
 * `egi - totalExpenses` - a deal nobody has touched does not move.
 */
export const yearOneNoi = (inputs: NoiProjectionInputs): NoiYear => projectNoiYear(inputs, 1);

/** The analysis-row fields that decide what a hold year earns. */
export type NoiFields = Partial<Pick<DealAnalysis,
  | 'income_growth_rate' | 'expense_growth_rate' | 'noi_overrides' | 'lease_up_months'
  | 'property_taxes' | 'property_tax_growth_rate' | 'expenses_are_carry'>>;

/** The form stores what was typed; a rate field that reads "2" on screen can arrive as "2". */
const num = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return 0;
  const parsed = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(parsed) ? parsed : 0;
};
/**
 * ⛔ NULL and ZERO are different answers for the tax growth rate: null means "same as expenses",
 * 0 means Prop 13 has been switched off on purpose. `num()` cannot tell them apart.
 */
const optionalRate = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const parsed = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * ⭐⭐⭐ ONE BUILDER FOR THE NOI INPUTS, which every surface SPREADS into `generateProjections` and
 * hands to `yearOneNoi`. The PDF used to assemble these by hand and left out `leaseUpMonths`,
 * `propertyTaxes` and `taxGrowthRate` - so a lease-up deal exported a stabilised year 1 beside a
 * screen showing $0. A field cannot be omitted from a list nobody writes by hand.
 *
 * @param noiOverrides the schedule the caller was handed (the PDF's option), which outranks the row.
 */
export function noiProjectionInputs(args: {
  data: NoiFields;
  egi: number;
  totalExpenses: number;
  noiOverrides?: NoiScheduleLike;
}): NoiProjectionInputs {
  const { data, egi, totalExpenses } = args;
  return {
    initialIncome: egi,
    initialExpenses: totalExpenses,
    incomeGrowthRate: num(data.income_growth_rate),
    expenseGrowthRate: num(data.expense_growth_rate),
    noiOverrides: args.noiOverrides ?? data.noi_overrides ?? undefined,
    leaseUpMonths: num(data.lease_up_months),
    propertyTaxes: num(data.property_taxes),
    taxGrowthRate: optionalRate(data.property_tax_growth_rate),
    expensesAreCarry: data.expenses_are_carry === true,
  };
}

/**
 * ⭐⭐ THE FINAL HOLD YEAR'S NOI, IN ONE PLACE, because two places is how this goes wrong.
 *
 * 🚨 The screen and the PDF each derived their own exit figure, and the PDF once printed DSCR
 * 3.52x against 3.62x on the screen for exactly that reason - found by rendering the document and
 * reading it, not by a test. A sabotage that made the PDF grow its exit a year past the screen's
 * passed the entire suite until this existed. **A rule that lives in two files is two rules.**
 */
export const finalHoldYearNoi = (
  projections: Array<{ noi: number }> | null | undefined,
  fallback: number
): number => {
  if (!Array.isArray(projections) || projections.length === 0) return fallback;
  const last = projections[projections.length - 1];
  /**
   * ⭐⭐ A PROJECTION OF ZERO IS A FIGURE, and so is a negative one (JJ2). The old `> 0` guard
   * sent a carried asset's $0 final year, and a vacant building's negative one, back to the
   * UN-GROWN year-1 fallback: the untouched Victorville screen priced its exit on -$100,443
   * while its own proforma printed -$110,870 for year 5. Only "no projection" falls back.
   */
  const noi = last?.noi;
  return typeof noi === 'number' && Number.isFinite(noi) ? noi : fallback;
};

export interface ExitNoiInput {
  /** The applied contractual schedule, if any. */
  applied: NoiScheduleLike;
  /**
   * The MODELLED final hold year's NOI - the last year you own it, from the growth model.
   *
   * ⭐⭐ RENAMED FROM `grown`, AND THE MEANING CHANGED WITH IT (Joseph's ruling 2026-08-30). It
   * used to be `lastYear` grown ONE MORE YEAR, so the same deal priced its exit on year N when a
   * schedule was applied and year N+1 when it was not. **Two exit conventions on one deal,
   * selected by whether somebody pressed Apply** - the defect class this build produces most.
   *
   * ⛔ Neither figure was ever the more "known" one: on this path the final hold year is modelled
   * and so is year N+1. So the code was not choosing between certain and uncertain, only being
   * inconsistent. ⭐ SIX delivered analyses price the final hold year - Long Beach, Bakersfield,
   * Moorpark, Delano, Jackson and STIIIZY all read "Exit Year NOI (Y5)".
   *
   * ⚠️ A parameter named `grown` that is no longer grown would be the same defect in miniature -
   * the words saying one thing while the maths does another - so the name moved with the rule.
   */
  finalHoldYearNoi: number;
  /** An OM's stated stabilized NOI, used as a FLOOR on NNN deals. */
  proFormaNoi?: number | null;
  leaseType?: string | null;
}

export interface ExitNoiResult {
  value: number;
  source: 'contract' | 'proforma' | 'growth';
}

/**
 * The exit-year NOI.
 *
 * ⭐⭐ THE EXIT PRICES ON THE FINAL HOLD YEAR - the last year you own it - not on a year grown
 * past it. Joseph's delivered analyses all do this: STIIIZY's exit table reads "Exit Year NOI
 * (Y5) $442,483", and `goldenDeals.test.ts` uses the last element of its own 5-year schedule
 * (Bear Valley, $327,024). ⚠️ I made the opposite mistake myself in the corpus reconciliation
 * and came out 0.61pp high on IRR before his document corrected me.
 *
 * ⛔ Growing the last year by a GENERIC rate on a lease with CONTRACTUAL steps is the same error
 * the NNN income-growth rule already forbids: "NNN leases have contractually fixed rents between
 * bump dates. Don't use generic 3% growth."
 */
export const exitNoi = (input: ExitNoiInput): ExitNoiResult => {
  const { applied, finalHoldYearNoi, proFormaNoi, leaseType } = input;

  if (Array.isArray(applied) && applied.length > 0) {
    const last = applied[applied.length - 1];
    if (usable(last)) return { value: last, source: 'contract' };
  }

  // The OM's stabilized figure is a FLOOR on an NNN deal, not a ceiling - every contractual bump
  // has landed by exit. Preserved from the shipped behaviour rather than quietly dropped.
  if (leaseType === 'nnn' && usable(proFormaNoi) && proFormaNoi > finalHoldYearNoi) {
    return { value: proFormaNoi, source: 'proforma' };
  }
  return { value: finalHoldYearNoi, source: 'growth' };
};

/**
 * ⭐⭐ THE CAPTION THAT GOES WITH THE FIGURE. `exitNoi` chooses between three sources and the
 * year differs by source, so a hardcoded "Year N+1" is right for exactly one of them.
 *
 * 🚨 It printed "Same Year 6 NOI of $442,483" on a 5-year hold - the correct FINAL-HOLD-YEAR
 * figure under the wrong year - because the number moved and the words did not. Joseph's
 * delivered table reads "Exit Year NOI (Y5)". Deriving the label here is what keeps the two
 * from drifting apart again.
 */
export const exitNoiYearLabel = (source: ExitNoiResult['source'], holdingPeriod: number): string => {
  if (source === 'proforma') return 'pro forma';
  /**
   * ⭐ BOTH the contract and the modelled path now name the FINAL HOLD YEAR - the last year you
   * own it. The growth branch used to read `holdingPeriod + 1` on the theory that a buyer at exit
   * is purchasing the next year's income. Defensible in the abstract, and not what any of the six
   * delivered analyses do. One rule, one caption.
   */
  return `Year ${holdingPeriod}`;
};

/** What the cap and the NOI beside it are struck on, and the words for it on every surface. */
export interface CapBasisCaption {
  basis: 'In-Place' | 'Stabilised';
  /** The metric strip and the key-metric card. */
  capRate: string;
  /** The header card, the scorecard row and the price-sensitivity column. */
  goingInCap: string;
  /** The NOI the cap is struck on, as the PDF's header row names it. */
  noi: string;
  /** The word after the figure in the PDF's opening sentence: "$4.29M ask @ 9.00% cap". */
  band: string;
}

/**
 * ⭐⭐ KK3. THE CAP'S CAPTION IS DERIVED FROM THE LEASE-UP, as `exitNoiYearLabel` derives the exit
 * year from its source. The cap is struck on the in-place NOI - the RATE the property earns, never
 * a year of collections - and on a lease-up deal that rate is the STABILISED one: the market rent
 * entered as income, which year 1 does not collect. His Victorville document reconciles at
 * "stabilised cap 5.70%" beside "Year 1 NOI $0"; the app printed the same 5.70% under
 * "Going-In Cap" beside the same $0, and the two captions contradicted each other on one screen.
 * ⛔ The NUMBER does not move here. Only the words do, and they move on every surface at once.
 */
export const capBasisCaption = (leaseUpMonths: number | null | undefined): CapBasisCaption =>
  typeof leaseUpMonths === 'number' && Number.isFinite(leaseUpMonths) && leaseUpMonths > 0
    ? { basis: 'Stabilised', capRate: 'Stabilised Cap', goingInCap: 'Stabilised Cap', noi: 'Stabilised NOI', band: 'stabilised cap' }
    : { basis: 'In-Place', capRate: 'Cap Rate', goingInCap: 'Going-In Cap', noi: 'In-Place NOI', band: 'cap' };

export interface NoiScheduleFromRentInput {
  /** Contractual RENT by hold year, index 0 = year 1. `null` = not contractually known. */
  annualRent: Array<number | null>;
  /** The screen's in-place NOI - what the property earns TODAY, net of its own expense model. */
  inPlaceNoi: number;
  /**
   * The contractual annual rent RATE in force on the same day `inPlaceNoi` describes.
   * ⭐ `annualRentRateAsOf` in `leaseScheduleEngine.ts`. NOT year 1 of the schedule.
   */
  inPlaceRentRate: number | null;
  /** Year-1 modelled operating expenses, on the same basis as `inPlaceNoi`. */
  totalExpenses: number;
  /** Annual expense growth as a FRACTION - 0.03, not 3. */
  expenseGrowthRate: number;
  /** The property-tax portion of `totalExpenses`. ▶ `expenseGrowth.ts`. */
  propertyTaxes?: number;
  /** Tax growth as a FRACTION. Absent means "same as expenses" - the previous behaviour. */
  taxGrowthRate?: number | null;
  /**
   * ⭐⭐ OTHER INCOME - expense recoveries, percentage rent, parking - as a year-1 figure.
   *
   * 🚨 It lives inside `inPlaceNoi` and was therefore held FLAT for the whole hold while expenses
   * grew beside it. On Stagecoach that understated NOI by $2,231 by year 5, and it is the LAST
   * line of that deal's gap. ⭐ Economically the recoveries should track the expenses they
   * reimburse: freezing them while expenses rise understates NOI by construction on every deal
   * with a recovery line.
   */
  otherIncome?: number;
  /** Growth for that line as a FRACTION. Absent means FLAT - the previous behaviour. */
  otherIncomeGrowthRate?: number | null;
}

export interface NoiScheduleFromRentResult {
  noiSchedule: Array<number | null>;
  warnings: string[];
}

/**
 * ⭐⭐ THE FOURTH ANCHOR: turning a contractual RENT schedule into an NOI schedule.
 *
 *   NOI(y) = inPlaceNoi + (rent(y) - rateToday) - (expenses(y) - expenses(1))
 *
 * Anchoring on the in-place NOI rather than using rent(y) directly is deliberate: this screen's
 * NOI already carries vacancy, reimbursements and expenses, and Bear Valley's saved analysis
 * runs 7% vacancy and $55,000 of taxes, so its NOI is NOT the OM's headline rent. For a true NNN
 * deal with zero landlord expenses the identity reduces to NOI(y) = rent(y), which is what the
 * reference guide shows and what Joseph's delivered analyses print.
 *
 * ⛔ THE DEFECT THIS FIXES (backlog A1). `rateToday` used to be `rent(1)` - HOLD YEAR 1 - while
 * `inPlaceNoi` is TODAY. The same year only when the close is this year, so once `close_date`
 * pushed year 1 forward the whole schedule sat ONE ESCALATION LOW. STIIIZY opened at $386,355
 * against a delivered $396,980, and every downstream figure inherited it.
 *
 * ⛔ AND `rateToday` IS A RATE, NOT A YEAR. Deriving it from a one-year schedule starting today
 * blends across the next step and is wrong in a way that looks right - it was tried, it landed
 * $389,258, and it was reverted.
 */
export const noiScheduleFromRent = (input: NoiScheduleFromRentInput): NoiScheduleFromRentResult => {
  const { annualRent, inPlaceNoi, inPlaceRentRate, totalExpenses, expenseGrowthRate } = input;
  const warnings: string[] = [];

  // ⛔ REFUSE, never fall back to year 1. Falling back IS the defect, and it is invisible.
  if (typeof inPlaceRentRate !== 'number' || !isFinite(inPlaceRentRate) || inPlaceRentRate < 0) {
    warnings.push(
      'The rent in force today could not be read from the leases, so contractual growth cannot be anchored and no schedule is offered.'
    );
    return { noiSchedule: annualRent.map(() => null), warnings };
  }

  const growth = isFinite(expenseGrowthRate) ? expenseGrowthRate : 0;
  const noiSchedule = annualRent.map((rent, i) => {
    if (typeof rent !== 'number' || !isFinite(rent)) return null;
    // ⭐ Taxes grow at their own rate when one is supplied - Prop 13 caps them at 2% while the
    // rest of the schedule runs with inflation. Absent, this is `total * (1 + g)^i` as before.
    const expensesYear = grownExpenses({
      totalExpenses,
      propertyTaxes: input.propertyTaxes ?? 0,
      expenseGrowthRate: growth,
      taxGrowthRate: input.taxGrowthRate ?? null,
      periods: i,
    });
    /**
     * ⭐ Other income grows on the same footing as the expense term: as a DELTA from year 1,
     * because `inPlaceNoi` already contains the year-1 figure. Absent rate = flat = as before.
     */
    const otherIncome = input.otherIncome ?? 0;
    const oiGrowth = typeof input.otherIncomeGrowthRate === 'number' && isFinite(input.otherIncomeGrowthRate)
      ? input.otherIncomeGrowthRate : 0;
    const otherIncomeYear = otherIncome * Math.pow(1 + oiGrowth, i);
    return inPlaceNoi
      + (rent - inPlaceRentRate)
      + (otherIncomeYear - otherIncome)
      - (expensesYear - totalExpenses);
  });

  return { noiSchedule, warnings };
};
